import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaChevronLeft, FaChevronRight, FaFire, FaStar } from "react-icons/fa";

import { getAllBanners } from "../../services/HomePage/BannerApi";

import useEmblaCarousel from "embla-carousel-react";

export default function OfferForYou() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imgErrors, setImgErrors] = useState({});
  const [isReady, setIsReady] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const navigate = useNavigate();
  const autoplayRef = useRef(null);

  const BASE_IMAGE_URL = process.env.REACT_APP_BANNER_IMAGE_URL || "https://admintest.medrphalabs.com/BannerImage/";

  // ✅ Embla Carousel - No loop
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      align: "start",
      containScroll: "trimSnaps",
      loop: false,
      dragFree: false,
      slidesToScroll: 1,
      speed: 10,
      breakpoints: {
        '(min-width: 768px)': { slidesToScroll: 2 },
        '(min-width: 1024px)': { slidesToScroll: 3 }
      }
    }
  );

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  // ✅ Update current index on slide change
  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setCurrentIndex(emblaApi.selectedScrollSnap());
    };

    emblaApi.on("select", onSelect);
    onSelect();

    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  // ✅ Auto-slide function - stops at end
  const startAutoplay = useCallback(() => {
    if (!emblaApi || !isReady || banners.length === 0) return;

    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
    }

    autoplayRef.current = setInterval(() => {
      if (emblaApi) {
        const current = emblaApi.selectedScrollSnap();
        const total = emblaApi.scrollSnapList().length - 1;
        
        if (current >= total) {
          clearInterval(autoplayRef.current);
          autoplayRef.current = null;
          return;
        }
        
        emblaApi.scrollNext();
      }
    }, 3000);
  }, [emblaApi, isReady, banners]);

  // ✅ Start autoplay when ready
  useEffect(() => {
    if (emblaApi && isReady && banners.length > 0) {
      setTimeout(() => {
        emblaApi.reInit();
        startAutoplay();
      }, 300);
    }
  }, [emblaApi, isReady, banners, startAutoplay]);

  // ✅ Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
        autoplayRef.current = null;
      }
    };
  }, []);

  // ✅ Fetch banners
  useEffect(() => {
    let isMounted = true;
    const fetchBanners = async () => {
      try {
        setLoading(true);
        const data = await getAllBanners(7);
        const list = Array.isArray(data) ? data : [];
        
        // Filter only active banners that have an image string
        const activeBanners = list
          .filter((item) => item.isActive !== false && item.image && String(item.image).trim() !== "")
          .map((item) => {
            const raw = String(item.image).trim();
            let fullUrl = "";
            if (raw.startsWith("http")) {
              fullUrl = raw;
            } else if (raw.startsWith("BannerImage/")) {
              fullUrl = `https://admin.medrphalabs.com/${raw}`;
            } else {
              fullUrl = `https://admin.medrphalabs.com/BannerImage/${raw}`;
            }

            return {
              ...item,
              resolvedImageUrl: fullUrl,
            };
          });

        if (isMounted) {
          setBanners(activeBanners);
          setIsReady(true);
        }
      } catch (error) {
        console.error("OfferForYou Error:", error);
        if (isMounted) setBanners([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchBanners();

    return () => {
      isMounted = false;
    };
  }, []);

  // ✅ Click handler
  const handleBannerClick = (item, e) => {
    e.stopPropagation();
    if (item.labId && Number(item.labId) > 0) {
      navigate(`/offerforyou-details/${item.labId}`);
    } else if (Number(item.productCategoryId) > 0) {
      navigate(`/brand/${item.productCategoryId}`);
    }
  };

  if (loading) {
    return (
      <div className="px-4 md:px-8 py-6 bg-white rounded-2xl border border-slate-200/80 shadow-xs mt-3">
        <div className="flex justify-center gap-4 overflow-hidden">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="w-[220px] h-[260px] bg-slate-100 rounded-2xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  const validBanners = banners.filter((item, i) => !imgErrors[i]);

  if (validBanners.length === 0) {
    return null;
  }

  return (
    <div className="px-4 md:px-8 py-6 bg-white rounded-3xl border border-slate-200/80 shadow-xs my-6">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-2.5">
          <div className="w-2 h-7 bg-gradient-to-b from-red-600 to-rose-600 rounded-full" />
          <div>
            <h3 className="text-lg sm:text-xl md:text-2xl font-black text-slate-900 leading-tight">
              Special Deals For You
            </h3>
            <p className="text-xs text-slate-500 font-medium mt-0.5">
              Exclusive medical offers and partner lab discounts ({validBanners.length} available)
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 self-end sm:self-auto">
          {/* Left Arrow */}
          <button
            onClick={scrollPrev}
            className="w-9 h-9 rounded-full bg-slate-100 hover:bg-red-600 hover:text-white text-slate-700 flex items-center justify-center transition-all shadow-2xs cursor-pointer"
            aria-label="Previous slide"
          >
            <FaChevronLeft size={14} />
          </button>

          {/* Right Arrow */}
          <button
            onClick={scrollNext}
            className="w-9 h-9 rounded-full bg-slate-100 hover:bg-red-600 hover:text-white text-slate-700 flex items-center justify-center transition-all shadow-2xs cursor-pointer"
            aria-label="Next slide"
          >
            <FaChevronRight size={14} />
          </button>
        </div>
      </div>

      {/* Carousel */}
      <div className="relative w-full">
        <div className="overflow-hidden w-full" ref={emblaRef}>
          <div className="flex gap-3">
            {banners.map((item, i) => {
              if (imgErrors[i]) return null;
              const imgUrl = item.resolvedImageUrl;

              return (
                <div
                  key={item.id || i}
                  className="min-w-[220px] max-w-[220px] flex-shrink-0"
                >
                  <div
                    onClick={(e) => handleBannerClick(item, e)}
                    className={`relative w-[220px] h-[260px] 
                    rounded-xl overflow-hidden shadow-md hover:shadow-2xl 
                    transition-all duration-300 bg-white
                    ${Number(item.productCategoryId) === 0 && !item.labId ? 'cursor-default' : 'cursor-pointer'}`}
                  >
                    <img
                      src={imgUrl}
                      alt={item.title || "Special Deal Banner"}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={() => setImgErrors(prev => ({ ...prev, [i]: true }))}
                      loading="lazy"
                    />
                    {/* Border effect */}
                    <div className="absolute inset-0 rounded-xl border-2 border-transparent hover:border-red-400/40 transition-all duration-300" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Trust Badges */}
      <div className="mt-8 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="flex items-center justify-center gap-2 text-xs text-gray-600 bg-gray-50 rounded-lg p-2 hover:bg-red-50 transition-colors">
            <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600 text-[10px] font-bold">✓</span>
            </div>
            <span>Verified Offers</span>
          </div>
          <div className="flex items-center justify-center gap-2 text-xs text-gray-600 bg-gray-50 rounded-lg p-2 hover:bg-red-50 transition-colors">
            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 text-[10px] font-bold">⚡</span>
            </div>
            <span>Limited Time</span>
          </div>
          <div className="flex items-center justify-center gap-2 text-xs text-gray-600 bg-gray-50 rounded-lg p-2 hover:bg-red-50 transition-colors">
            <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
              <span className="text-purple-600 text-[10px] font-bold">🏆</span>
            </div>
            <span>Best Deals</span>
          </div>
          <div className="flex items-center justify-center gap-2 text-xs text-gray-600 bg-gray-50 rounded-lg p-2 hover:bg-red-50 transition-colors">
            <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
              <span className="text-red-600 text-[10px] font-bold">❤️</span>
            </div>
            <span>Customer Favorites</span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </div>
  );
}